<script setup lang="ts">
import { computed, markRaw, onBeforeMount, onBeforeUnmount, ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import SlotDialog from './ui/modal/SlotDialog.vue'
import InactivitySlot from './ui/modal/inactivity/InactivitySlot.vue'
import { useAppStore } from '@stores/app.store'
import { isPlaywright } from '@lib/utils/testUtils'
import { scopedLogger, logInfo } from '@lib/utils/logUtils'
import { useGtm } from '@composables/useGtm'
import { useListen } from '@composables/useEventBus'
import ConfirmDialog from './ui/modal/ConfirmDialog.vue'
import { useLocale } from '@composables/useLocale'
import { useCartActions } from '@composables/useCartActions'
import { GTM_INTERACTION_COMPONENTS, LogoutType } from '@lib/models/gtm'
import { TRANSLATIONS_MAPPER } from '@lib/models/internal/translations'

const log = scopedLogger('@component/InteractionKeepAlive')

const STANDARD_INACTIVITY_SECONDS = 120
const STANDARD_INACTIVITY_ALERT_SECONDS = 30

const store = useAppStore()
const router = useRouter()
const route = useRoute()
const {
  trackInactivityPopup,
  trackInactivityExit,
  trackLogout,
  trackLogoutConfirmation,
  trackPackLogoutAndExit,
  trackInteraction
} = useGtm()
const { t } = useLocale()
const { sendReset } = useCartActions()

const dialog = ref(false)
const interval = ref()
const timeout = ref()
const unauthorizedState = ref(false)

const inactivitySeconds = computed(() => {
  if (isPlaywright()) return 5
  return store.config?.inactivitySeconds ?? STANDARD_INACTIVITY_SECONDS
})

const alertSeconds = computed(() => {
  if (isPlaywright()) return 5
  return store.config?.inactivityAlertSeconds ?? STANDARD_INACTIVITY_ALERT_SECONDS
})

const DOUBLE_TIME_INACTIVITY_MULTIPLIER = 2

// Screens where the user needs more time than usual before the inactivity alert shows up
// (e.g. `payment` polls the order until it is paid). Add new entries here when needed.
const DOUBLE_TIME_INACTIVITY_SCREENS: { route: string; step?: string }[] = [
  { route: 'checkout', step: 'payment' },
  { route: 'otp-validation' }
]

const hasDoubleTimeInactivity = () => {
  const step = route.query.step?.toString()
  return DOUBLE_TIME_INACTIVITY_SCREENS.some(
    (screen) => screen.route === route.name && (!screen.step || screen.step === step)
  )
}

const standardIntervalMillis = computed(() => {
  const multiplier = hasDoubleTimeInactivity() ? DOUBLE_TIME_INACTIVITY_MULTIPLIER : 1
  return inactivitySeconds.value * multiplier * 1000
})

router.afterEach(() => {
  onUserInteraction()
})

const canCreateInterval = () => {
  if (['splash', 'attract', 'inoperative-printer'].includes(route.name as string)) return false
  if (route.name === 'checkout') {
    const step = route.query.step?.toString()
    if (
      step &&
      ['payment-method', 'withdrawal-method', 'table-service-number', 'payment'].includes(step)
    )
      return true
    else return false
  }
  return true
}

const reset = async () => {
  onUserInteraction()
  // will reset ADK ...
  store.resetADK = true
  await router.push({ name: 'splash' })
}

const closeInterval = () => {
  clearInterval(interval.value)
  interval.value = undefined
}

const closeTimeout = () => {
  clearTimeout(timeout.value)
  timeout.value = undefined
  dialog.value = false
}

const createInterval = () => {
  if (canCreateInterval()) {
    interval.value = setInterval(function () {
      onFinishedInterval()
    }, standardIntervalMillis.value)
  }
}

function onFinishedInterval() {
  logInfo(log, 'INACTIVE: Inactivity interval has ended, showing alert for closing session')
  closeInterval()
  dialog.value = true
  trackInactivityPopup()
  if (store.isUserFullExp) store.setInactivityIsShowed(true)

  timeout.value = setTimeout(function () {
    logInfo(log, 'INACTIVE: Timeout ended, user is inactive, session will be closed')
    trackInactivityExit()
    trackPackLogoutAndExit(LogoutType.INACTIVITY)
    unauthorizedState.value = false
    reset()
  }, alertSeconds.value * 1000)
}

function onUserInteraction() {
  closeTimeout()
  closeInterval()
  if (!interval.value) {
    createInterval()
  }
}

// Global clicks count as activity to keep the session alive, but only while the
// inactivity alert is NOT shown. Once it's shown, any interaction with the rest of
// the screen must be ignored — only the alert's own buttons (confirm/cancel) count.
function onGlobalInteraction() {
  if (dialog.value) return
  onUserInteraction()
}

const onUserConfirmToStay = async () => {
  trackInteraction({
    componentName: GTM_INTERACTION_COMPONENTS.INACTIVITY.componentName,
    linkText: GTM_INTERACTION_COMPONENTS.INACTIVITY.primaryText
  })
  logInfo(log, 'INACTIVE: User confirmed to stay, interval will be reset')
  onUserInteraction()
}

const onCancelOrder = async () => {
  trackInteraction({
    componentName: GTM_INTERACTION_COMPONENTS.INACTIVITY.componentName,
    linkText: GTM_INTERACTION_COMPONENTS.INACTIVITY.secondaryText
  })
  logInfo(log, 'INACTIVE: User cancelled the order from the inactivity alert')
  closeTimeout()
  closeInterval()

  if (store.cartUnits) trackLogoutConfirmation()
  await sendReset(LogoutType.CANCELLATION)
}

const onUnauthorizedEvent = async () => {
  if (unauthorizedState.value) return
  // the inoperative-printer screen is reached with the session already reset and has no user
  // to warn: a 401 here (e.g. from its own polling) must not open the "session expired" dialog
  if (route.name === 'inoperative-printer') return
  logInfo(log, 'unauthorized event (401)')
  await store.clearUserData({ cleanSession: true })
  trackLogout(LogoutType.UNAUTHORIZED)
  unauthorizedState.value = true
}

const onConfirmUnautorized = () => {
  unauthorizedState.value = false
}

useListen('customer:unauthorized', onUnauthorizedEvent)

onBeforeMount(() => {
  globalThis.addEventListener('click', onGlobalInteraction)
})

onBeforeUnmount(() => {
  globalThis.removeEventListener('click', onGlobalInteraction)
})
</script>

<template>
  <div data-test="interaction-keep-alive" class="absolute top-0 left-auto">
    <!-- InactivityAlert -->
    <SlotDialog
      v-if="dialog"
      :data="{ countdown: alertSeconds }"
      :slot-component="markRaw(InactivitySlot)"
      class="z-12!"
      @confirm="onUserConfirmToStay"
      @cancel="onCancelOrder"
    />
    <!-- Unauthorized state dialog -->
    <ConfirmDialog
      v-if="unauthorizedState"
      :data-test="'confirm-dialog--unauthorized'"
      :title="t(TRANSLATIONS_MAPPER.MODAL.IDENTIFICATION.unauthorized.title)"
      :description="t(TRANSLATIONS_MAPPER.MODAL.IDENTIFICATION.unauthorized.description)"
      :primary-button="t('Volver')"
      class="z-11!"
      @confirm="onConfirmUnautorized"
    />
  </div>
</template>
